import { ApiError, NetworkError, detailNumber, detailString } from "./error";

/**
 * Что UI делает с ошибкой. Чистая функция без единой ссылки на React —
 * иначе политику пришлось бы проверять через рендер, а её ветки должны быть
 * покрыты юнит-тестами по одной.
 */
export type Reaction =
  /** Молчаливый релогин и один повтор. Refresh-токена нет by design. */
  | { kind: "relogin" }
  /** Пользователь заблокирован (403). Токен стирается, роут — экран блокировки. */
  | { kind: "blocked" }
  /** Ресурс не найден или чужой (бэкенд отдаёт 404, а не 403). */
  | { kind: "not-found" }
  /**
   * Денег не хватает. Экран пополнения, а НЕ тост с текстом ответа — ради
   * этого код и выделен отдельным от REQUEST_ERROR.
   * Покупка даёт `{balance, requested}`, вывод — `{available, requested}`.
   */
  | { kind: "insufficient"; available: string | undefined; requested: string | undefined }
  /** Ошибка ввода: показывается рядом с полем, не тостом. */
  | { kind: "inline"; message: string; details: Record<string, unknown> }
  /** Действие уже выполнено либо ещё выполняется. */
  | { kind: "conflict"; inProgress: boolean; message: string }
  /** Лимит запросов. Задержка — из details, заголовка Retry-After у бэкенда нет. */
  | { kind: "backoff"; retryAfterSeconds: number }
  /** Дефект фронта: отправили то, чего контракт не принимает. */
  | { kind: "bug"; message: string }
  /** Сервис недоступен. */
  | { kind: "server"; message: string }
  /** Нет сети — глобальный оверлей поверх текущего экрана. */
  | { kind: "offline" }
  /** Всё остальное — тост. */
  | { kind: "toast"; message: string };

export function classify(error: unknown): Reaction {
  if (error instanceof NetworkError) return { kind: "offline" };

  if (!(error instanceof ApiError)) {
    return { kind: "toast", message: error instanceof Error ? error.message : String(error) };
  }

  switch (error.code) {
    case "AUTH_ERROR":
      return { kind: "relogin" };

    case "FORBIDDEN":
      return { kind: "blocked" };

    case "NOT_FOUND":
      return { kind: "not-found" };

    case "INSUFFICIENT_FUNDS":
      return {
        kind: "insufficient",
        // Покупка робота отдаёт `balance`, вывод — `available`. Берём то,
        // что пришло, и показываем именно его: считать разницу на клиенте
        // нельзя (правило ТЗ №5).
        available: detailString(error.details, "available") ?? detailString(error.details, "balance"),
        requested: detailString(error.details, "requested"),
      };

    case "CONFLICT":
      return {
        kind: "conflict",
        inProgress: error.details["reason"] === "in_progress",
        message: error.message,
      };

    case "TOO_MANY_REQUESTS":
      return {
        kind: "backoff",
        // Корзина лимита считается по IP и по шаблону маршрута, поэтому
        // задержку надо соблюдать честно, а не ретраить сразу.
        retryAfterSeconds: detailNumber(error.details, "retry_after_seconds") ?? 60,
      };

    case "VALIDATION_ERROR":
      return { kind: "bug", message: error.message };

    case "REQUEST_ERROR":
      return { kind: "inline", message: error.message, details: error.details };

    case "EXTERNAL_SERVICE_ERROR":
    case "CONFIGURATION_ERROR":
    case "NOT_IMPLEMENTED":
      return { kind: "server", message: error.message };

    default:
      return error.status >= 500
        ? { kind: "server", message: error.message }
        : { kind: "toast", message: error.message };
  }
}
